import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatMoney } from '@nokowazzi/types';
import type { CurrencyCode } from '@nokowazzi/types';
import {
  Button,
  EmptyState,
  Input,
  RadioGroup,
  RadioItem,
  Spinner,
  Textarea,
  toast,
} from '@nokowazzi/ui';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { usePermissions } from '../features/admin/use-permissions';
import { adminDisputeDetailQuery } from '../features/disputes/queries';
import {
  DISPUTE_REASON_LABEL,
  DisputeStatusBadge,
  isOpenDispute,
  slaRemaining,
  toDisputeStatus,
} from '../features/disputes/status';
import { OrderStatusBadge } from '../features/orders/status';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';
import { imagePublicUrl } from '../lib/storage';

export const Route = createFileRoute('/_admin/disputes/$id')({
  component: DisputeDetailScreen,
});

type Decision = 'refund' | 'partial_refund' | 'release' | 'reject';

const DECISION_OPTIONS: { value: Decision; label: string; desc: string }[] = [
  { value: 'refund', label: '전액 환불', desc: '구매자에게 전액 환불' },
  { value: 'partial_refund', label: '부분 환불', desc: '일부 환불 후 거래 확정' },
  { value: 'release', label: '판매자 지급', desc: '거래 확정 후 정산' },
  { value: 'reject', label: '기각', desc: '분쟁 기각, 원상태 복귀' },
];

function DisputeDetailScreen() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { role, can } = usePermissions();
  const detailQuery = useQuery(adminDisputeDetailQuery(id));

  const [decision, setDecision] = useState<Decision>('refund');
  const [refundAmount, setRefundAmount] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ note?: string; refundAmount?: string }>({});

  const resolveMutation = useMutation({
    mutationFn: (input: { decision: Decision; refundAmount?: number; note: string }) =>
      apiFetch<unknown>(`/v1/admin/disputes/${id}/resolve`, { method: 'POST', json: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'disputes'] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      toast.show({ type: 'success', message: '분쟁을 처리했습니다' });
      setNote('');
      setRefundAmount('');
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '분쟁 처리에 실패했습니다',
      });
    },
  });

  const order = detailQuery.data?.order ?? null;

  const handleResolve = () => {
    const nextErrors: { note?: string; refundAmount?: string } = {};
    if (note.trim() === '') nextErrors.note = '사유를 입력해 주세요';
    let amount: number | undefined;
    if (decision === 'partial_refund') {
      amount = Number(refundAmount);
      if (refundAmount.trim() === '' || !Number.isFinite(amount) || amount <= 0) {
        nextErrors.refundAmount = '환불 금액을 입력해 주세요';
      } else if (order != null && amount >= order.total_amount) {
        nextErrors.refundAmount = '부분 환불액은 총액보다 작아야 합니다';
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    resolveMutation.mutate({
      decision,
      refundAmount: decision === 'partial_refund' ? amount : undefined,
      note: note.trim(),
    });
  };

  // 전액 환불 + CS 역할이면 즉시 실행이 아니라 승인 대기로 전환됨을 안내
  const showCsApprovalNotice = useMemo(
    () => decision === 'refund' && role === 'cs',
    [decision, role],
  );

  if (detailQuery.isPending) {
    return (
      <div className="flex min-h-52 items-center justify-center">
        <Spinner label="분쟁 불러오는 중" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <EmptyState
        title="분쟁을 불러오지 못했습니다"
        description={
          detailQuery.error instanceof ApiError ? detailQuery.error.message : '잠시 후 다시 시도해 주세요'
        }
        action={
          <Button variant="tertiary" size="sm" onClick={() => void detailQuery.refetch()}>
            다시 시도
          </Button>
        }
      />
    );
  }

  const { dispute, buyer, seller, evidence, chatMessages } = detailQuery.data;
  const status = toDisputeStatus(dispute.status);
  const currency = (order?.currency ?? 'KRW') as CurrencyCode;
  const canResolve = can('disputes:resolve') && isOpenDispute(status);
  const sla = isOpenDispute(status) ? slaRemaining(dispute.created_at) : null;

  return (
    <>
      <div className="mb-4">
        <Link
          to="/disputes"
          className="inline-flex items-center gap-1.5 text-caption1 text-fg-secondary hover:text-fg"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          분쟁 목록
        </Link>
      </div>

      <PageHeader
        title={`분쟁 ${dispute.order_id.slice(0, 8)}`}
        description={DISPUTE_REASON_LABEL[dispute.reason_code] ?? dispute.reason_code}
        actions={<DisputeStatusBadge status={dispute.status} />}
      />

      <div className="grid grid-cols-3 gap-6">
        {/* 1열: 분쟁 정보 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">분쟁 정보</h2>
          <dl className="flex flex-col gap-2 text-caption1">
            <Row label="사유">{DISPUTE_REASON_LABEL[dispute.reason_code] ?? dispute.reason_code}</Row>
            <Row label="접수일">{formatDateTime(dispute.created_at)}</Row>
            {sla != null && (
              <Row label="SLA">
                <span className={sla.overdue ? 'font-semibold text-danger-600' : 'text-fg'}>
                  {sla.label}
                </span>
              </Row>
            )}
            <Row label="구매자">{buyer?.display_name ?? '—'}</Row>
            <Row label="판매자">{seller?.display_name ?? '—'}</Row>
            {order != null && (
              <>
                <Row label="주문상태">
                  <OrderStatusBadge status={order.status} />
                </Row>
                <Row label="거래금액">
                  <span className="tabular-nums">
                    {formatMoney({ amount: order.total_amount, currency })}
                  </span>
                </Row>
                <Row label="주문">
                  <Link
                    to="/orders/$id"
                    params={{ id: order.id }}
                    className="text-fg-brand hover:underline"
                  >
                    주문 상세
                  </Link>
                </Row>
              </>
            )}
          </dl>
          {dispute.description != null && dispute.description !== '' && (
            <div className="mt-4 border-t border-line pt-3">
              <p className="mb-1 text-caption2 text-fg-tertiary">신고 내용</p>
              <p className="whitespace-pre-wrap text-caption1 text-fg-secondary">
                {dispute.description}
              </p>
            </div>
          )}
        </section>

        {/* 2열: 증빙 + 대화 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">증빙 · 대화</h2>
          <div className="mb-4">
            <p className="mb-2 text-caption2 text-fg-tertiary">증빙 이미지</p>
            {evidence.length === 0 ? (
              <p className="text-caption1 text-fg-tertiary">첨부된 증빙이 없습니다</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {evidence.map((path) => (
                  <a
                    key={path}
                    href={imagePublicUrl(path)}
                    target="_blank"
                    rel="noreferrer"
                    className="block overflow-hidden rounded-md border border-line"
                  >
                    <img
                      src={imagePublicUrl(path)}
                      alt="증빙 이미지"
                      className="aspect-square w-full object-cover"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="mb-2 text-caption2 text-fg-tertiary">채팅 원문</p>
            {chatMessages === undefined ? (
              <p className="text-caption1 text-fg-tertiary">채팅 열람 권한이 없습니다</p>
            ) : chatMessages.length === 0 ? (
              <p className="text-caption1 text-fg-tertiary">대화 내역이 없습니다</p>
            ) : (
              <div className="flex max-h-80 flex-col gap-2 overflow-y-auto rounded-md bg-subtle p-3">
                {chatMessages.map((m) => (
                  <div key={m.id} className="flex flex-col gap-0.5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-caption2 font-medium text-fg">{m.senderName ?? '시스템'}</span>
                      <span className="text-caption2 text-fg-tertiary">{formatDateTime(m.createdAt)}</span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-caption1 text-fg-secondary">
                      {m.body ?? '(내용 없음)'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* 3열: 결정 패널 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">결정</h2>
          {!isOpenDispute(status) ? (
            <div className="rounded-md bg-subtle p-4 text-caption1 text-fg-secondary">
              이미 처리된 분쟁입니다.
              {dispute.resolved_at != null && (
                <span className="mt-1 block text-caption2 text-fg-tertiary">
                  처리일 {formatDateTime(dispute.resolved_at)}
                </span>
              )}
            </div>
          ) : !can('disputes:resolve') ? (
            <p className="text-caption1 text-fg-tertiary">분쟁 해결 권한이 없습니다</p>
          ) : (
            <Can perm="disputes:resolve">
              <RadioGroup
                value={decision}
                onValueChange={(value) => {
                  setDecision(value as Decision);
                  setErrors({});
                }}
                className="gap-1"
              >
                {DECISION_OPTIONS.map((opt) => (
                  <RadioItem
                    key={opt.value}
                    value={opt.value}
                    label={
                      <span className="flex flex-col">
                        <span className="text-caption1 font-medium text-fg">{opt.label}</span>
                        <span className="text-caption2 text-fg-tertiary">{opt.desc}</span>
                      </span>
                    }
                  />
                ))}
              </RadioGroup>

              {decision === 'partial_refund' && (
                <div className="mt-3">
                  <Input
                    label="환불 금액"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    error={errors.refundAmount}
                    suffix={<span className="text-caption2">{currency}</span>}
                  />
                </div>
              )}

              <div className="mt-3">
                <Textarea
                  label="처리 사유 (필수)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  error={errors.note}
                  placeholder="결정 근거를 기록하세요"
                />
              </div>

              {showCsApprovalNotice && (
                <div className="mt-3 rounded-md bg-warning-50 p-3 text-caption2 text-warning-700">
                  전액 환불은 CS 권한으로 즉시 실행되지 않고 <b>승인 대기</b> 상태로 전환됩니다. 재무/최고
                  관리자의 2차 승인이 필요합니다.
                </div>
              )}

              <Button
                variant="danger"
                fullWidth
                className="mt-4"
                loading={resolveMutation.isPending}
                disabled={!canResolve}
                onClick={handleResolve}
              >
                {showCsApprovalNotice ? '승인 요청' : '결정 실행'}
              </Button>
            </Can>
          )}
        </section>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-fg-tertiary">{label}</dt>
      <dd className="text-right text-fg">{children}</dd>
    </div>
  );
}
